<template>
  <Modal
    sheetbelowsm
    :title="featureConfig ? featureName : $t('projects.ui_features.enable', { feature: featureName })"
    :primary-button="featureConfig ? $t('save') : $t('enable')"
    :secondary-button="$t('cancel')"
    :primary-button-loading="loading"
    :primary-button-disabled="saveDisabled"
    @primary="save"
    @secondary="cancel"
    @close="cancel"
  >
    <div class="sm:w-600 flex py-5 px-10 flex-wrap" :class="{ 'h-450': ['conversations', 'collections'].includes(feature) }">
      <div class="w-full">
        <div class="mb-4">{{ description }}</div>
        <template v-if="feature === 'conversations'">
          <ValidationObserver ref="validationObserver" class="w-full flex flex-col gap-y-3 pb-5">
            <FormSelect v-model="config.agents" multiple default-reducer :options="agents" label="Agents" append-to-body class="w-full" @update:modelValue="onAgentsChange" />
            <div class="text-12 text-gray-500 -mt-2">End users pick which of these agents to chat with, and can switch mid-conversation.</div>
            <FormSelect v-model="config.defaultAgent" default-reducer :options="defaultAgentOptions" label="Default agent" append-to-body class="w-full" />
            <Checkbox label="Allow users to upload PDF files" v-model="config.pdfUpload" class="mt-2" />
            <Checkbox label="Allow users to upload Image files" v-model="config.imageUpload" class="mt-2" />
            <Checkbox label="Allow users to upload Excel / CSV files" v-model="config.excelUpload" class="mt-2" />
            <div class="text-12 text-gray-500 mt-1">Uploaded files are sent directly to the agent in the conversation.</div>
          </ValidationObserver>
        </template>
        <template v-else-if="feature === 'collections'">
          <ValidationObserver ref="validationObserver" class="w-full flex flex-col gap-y-3 pb-5">
            <FormSelect v-model="config.agents" multiple default-reducer :options="agents" label="Agents" append-to-body class="w-full" @update:modelValue="onAgentsChange" />
            <div class="text-12 text-gray-500 -mt-2">End users pick which of these agents runs their Collections queries, and can switch when more than one is available.</div>
            <FormSelect v-model="config.defaultAgent" default-reducer :options="defaultAgentOptions" label="Default agent" append-to-body class="w-full" />
            <div class="flex gap-x-3 items-start">
              <FormSelectMultiBadge
                v-model="config.email.from"
                label="Digest sender email (Optional)"
                :options="emailFromList"
                searchable
                clearable
                :class="fromIsDomain ? 'w-1/2' : 'w-full'"
              ></FormSelectMultiBadge>
              <div v-if="fromIsDomain" class="w-1/2 flex flex-row items-center">
                <FormInput v-model="config.email.fromEmail" name="from_em" label="Sender mailbox" class="flex-grow" />
                <div class="text-13 mt-4 ml-1 whitespace-nowrap">@{{ config.email.from.value }}</div>
              </div>
            </div>
          </ValidationObserver>
        </template>
        <template v-else-if="feature === 'data_sources'">
          <div v-if="projectDataAdapters.length">
            <div v-for="item in projectDataAdapters" :key="item.adapter_type" class="flex items-center w-full mt-2">
              <Checkbox :label="item.name" :model-value="hasConfigDataAdapter(item.adapter_type)" @update:modelValue="onDataAdapterChange($event, item.adapter_type)" />
            </div>
          </div>
          <div v-else>
            <div class="flex items-center w-full mt-2">No available data adapters for this feature</div>
          </div>
        </template>
        <template v-else-if="feature === 'profile'">
          <div class="flex items-center w-full mt-2">Confirm you would like to enable this feature</div>
        </template>
        <template v-else-if="feature === 'deep_research_reports'">
          <div v-for="item in config.reports" :key="item.value" class="flex flex-col items-center w-full mt-3">
            <Checkbox :label="item.label" v-model="item.enabled" class="w-full" />
            <FormSelect v-if="item.enabled" v-model="item.flow" default-reducer :key="item.value" :options="flows" label="Flow" append-to-body class="w-full mt-2" />
            <FormInput v-if="item.enabled" v-model="item.title" default-reducer label="Title" append-to-body class="w-full mt-2" />
            <FormInput v-if="item.enabled" v-model="item.description" default-reducer label="Description" append-to-body class="w-full mt-2" />
          </div>
        </template>
        <template v-if="feature === 'data_transformation'">
          <ValidationObserver ref="validationObserver" class="w-full flex flex-col gap-y-3 pb-5">
            <FormSelect v-model="config.flow" default-reducer :options="flows" label="Flow" append-to-body class="w-full" />
            <Checkbox label="Allow users to upload Excel files for source data" v-model="config.inputExcelUpload" class="mt-2" />
            <VariableSelect
              v-if="config.inputExcelUpload"
              v-model="config.inputExcelUploadVariable"
              label="Assign uploaded Excel file to variable"
              :filter-data-type="['Excel']"
              class="w-full"
              :system="false"
              :placeholder="$t('evaluate.select_variable')"
            ></VariableSelect>
            <Checkbox label="Allow users to upload Excel files to be used as output template" v-model="config.outputTemplateExcelUpload" class="mt-2" />
            <VariableSelect
              v-if="config.outputTemplateExcelUpload"
              v-model="config.outputTemplateExcelUploadVariable"
              label="Assign uploaded Excel file to variable"
              :filter-data-type="['Excel']"
              class="w-full"
              :system="false"
              :placeholder="$t('evaluate.select_variable')"
            ></VariableSelect>
            <Checkbox label="Allow users to edit Exchange Rates" v-model="config.editExchangeRates" class="mt-2" />
            <VariableSelect
              v-if="config.editExchangeRates"
              v-model="config.exchangeRatesVariable"
              label="Assign Exchange Rates to variable"
              :filter-data-type="['DataTable']"
              class="w-full"
              :system="false"
              :placeholder="$t('evaluate.select_variable')"
            ></VariableSelect>
            <Checkbox label="Allow users to edit Years to Include" v-model="config.editYearsToInclude" class="mt-2" />
            <VariableSelect
              v-if="config.editYearsToInclude"
              v-model="config.yearsToIncludeVariable"
              label="Assign Years to variable"
              :filter-data-type="['DataTable']"
              class="w-full"
              :system="false"
              :placeholder="$t('evaluate.select_variable')"
            ></VariableSelect>
            <Checkbox label="Allow users to edit Business Unit" v-model="config.editBusinessUnit" class="mt-2" />
            <VariableSelect
              v-if="config.editBusinessUnit"
              v-model="config.businessUnitVariable"
              label="Assign Business Unit to variable"
              :filter-data-type="['Text']"
              class="w-full"
              :system="false"
              :placeholder="$t('evaluate.select_variable')"
            ></VariableSelect>
            <Checkbox label="Allow users to edit Product Manager" v-model="config.editProductManager" class="mt-2" />
            <VariableSelect
              v-if="config.editProductManager"
              v-model="config.productManagerVariable"
              label="Assign Product Manager to variable"
              :filter-data-type="['Text']"
              class="w-full"
              :system="false"
              :placeholder="$t('evaluate.select_variable')"
            ></VariableSelect>
          </ValidationObserver>
        </template>
      </div>
    </div>
  </Modal>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

const FeatureConfig = {
  conversations: {
    enabled: true,
    config: {
      agents: [],
      defaultAgent: '',
      pdfUpload: false,
      imageUpload: false,
      excelUpload: false,
    },
  },
  collections: {
    enabled: true,
    config: {
      agents: [],
      defaultAgent: '',
      email: { from: null, fromEmail: '' },
      digest_template: 'COLLECTIONS_DIGEST',
    },
  },
  profile: { enabled: true, config: {} },
  data_sources: {
    enabled: true,
    config: {
      adapters: [],
    },
  },
  deep_research_reports: {
    enabled: true,
    config: {
      reports: [
        {
          value: 'ORGANISATION_OPPORTUNITY_REPORT',
          icon: 'opportunity',
          label: 'Organisation Opportunity Scanner Report',
          enabled: false,
          flow: '',
          title: 'Organisation Opportunity Scanner Report',
          description: '',
        },
        {
          value: 'ORGANISATION_STRATEGY_REPORT',
          icon: 'domain',
          label: 'Organisation Market Strategy Report',
          enabled: false,
          flow: '',
          title: 'Organisation Market Strategy Report',
          description: '',
        },
        // { value: 'EMPLOYEE_GROWTH_REPORT', icon: 'badge', label: 'Employee Growth Report', enabled: false, flow: '', title: 'Employee Growth Report', description: '' },
      ],
    },
  },
  data_transformation: {
    enabled: true,
    config: {
      flow: '',
      inputExcelUpload: false,
      inputExcelUploadVariable: '',
      outputTemplateExcelUpload: false,
      outputTemplateExcelUploadVariable: '',
      editExchangeRates: false,
      exchangeRatesVariable: '',
      editYearsToInclude: false,
      yearsToIncludeVariable: '',
      editBusinessUnit: false,
      businessUnitVariable: '',
      editProductManager: false,
      productManagerVariable: '',
    },
  },
};

export default {
  name: 'EnableUIFeatureModal',
  data() {
    return {
      fetching: true,
      loading: false,
      selected: null,
      config: {},
    };
  },
  emits: ['save', 'close'],
  props: {
    feature: {
      type: String,
      required: true,
    },
    featureName: {
      type: String,
      required: true,
    },
    description: {
      type: String,
    },
    featureConfig: {
      type: Object,
    },
  },
  async mounted() {
    if (['conversations', 'collections', 'data_transformation'].includes(this.feature)) {
      await this.fetchProjectVariables({ projectId: this.projectId });
    }
    if (this.feature === 'collections') {
      // Collections only needs the email providers (digest sender); the selectable
      // agents come from the project flows already loaded in the store.
      await this.fetchProjectEmailProviders({ projectId: this.projectId });
    }
  },
  async created() {
    this.config = {
      ...structuredClone(FeatureConfig[this.feature]),
    };
    if (this.feature === 'deep_research_reports' && this.featureConfig?.config?.reports) {
      for (let i = 0, { length } = this.featureConfig.config.reports; i < length; i++) {
        if (this.featureConfig.config.reports[i].enabled && this.featureConfig.config.reports[i].flow) {
          this.config.config.reports = this.config.config.reports.map((item) => {
            if (item.value === this.featureConfig.config.reports[i].value) {
              item.flow = this.featureConfig.config.reports[i].flow;
              item.title = this.featureConfig.config.reports[i].title;
              item.description = this.featureConfig.config.reports[i].description;
              item.enabled = this.featureConfig.config.reports[i].enabled;
            }
            return item;
          });
        }
      }
    } else {
      this.config = {
        ...this.config,
        ...structuredClone(this.featureConfig),
      };
    }
    this.config = this.config.config;
    if (this.feature === 'collections') {
      if (!this.config.email || typeof this.config.email !== 'object') this.config.email = { from: null, fromEmail: '' };
      if (!Array.isArray(this.config.agents)) this.config.agents = [];
      if (typeof this.config.defaultAgent !== 'string') this.config.defaultAgent = '';
      this.config.digest_template = 'COLLECTIONS_DIGEST';
    }
  },
  computed: {
    ...mapState(['availableDataAdapters']),
    ...mapGetters(['getProjectFlows', 'getProjectDataAdapters', 'getProjectEmailProviders']),
    saveDisabled() {
      // if (['data_sources', 'conversations', 'collections'].includes(this.feature) && (this.projectDataAdapters.length === 0 || this.config.adapters.length === 0)) {
      //   return true;
      // }
      if (this.feature === 'conversations' && (!this.config.agents?.length || !this.config.defaultAgent || !this.config.agents.includes(this.config.defaultAgent))) {
        return true;
      }
      if (this.feature === 'collections' && (!this.config.agents?.length || !this.config.defaultAgent || !this.config.agents.includes(this.config.defaultAgent))) {
        return true;
      }
      if (this.feature === 'deep_research_reports') {
        for (let i = 0, { length } = this.config.reports; i < length; i++) {
          if (this.config.reports[i]?.enabled && (!this.config.reports?.[i]?.flow || !this.config.reports?.[i]?.title || !this.config.reports?.[i]?.description)) {
            return true;
          }
        }
      }
      return false;
    },
    projectDataAdapters() {
      return Object.values(this.getProjectDataAdapters()).map((item) => {
        return {
          ...item,
          name: this.availableDataAdapters[item.adapter_type].name,
        };
      });
    },
    projectId() {
      return this.$route.params.projectId;
    },
    flows() {
      return Object.values(this.getProjectFlows(this.projectId)).map((flow) => ({
        label: flow.name,
        value: flow.flow_id,
      }));
    },
    agents() {
      return Object.values(this.getProjectFlows(this.projectId))
        .filter((flow) => flow.flowType === 'Agent')
        .map((flow) => ({ label: flow.name, value: flow.flow_id }));
    },
    // The default-agent dropdown only offers agents that are currently selected.
    defaultAgentOptions() {
      const selected = this.config.agents || [];
      return this.agents.filter((a) => selected.includes(a.value));
    },
    emailFromList() {
      return Object.values(this.getProjectEmailProviders()).reduce((acc, item) => {
        const accounts = item?.config?.accounts || [];
        return [
          ...acc,
          ...accounts.map((account) => ({
            label: account.email || account.domain,
            value: account.email || account.domain,
            provider: item.provider,
            badge1: account.email ? 'email' : 'domain',
            badge2: item.provider,
          })),
        ];
      }, []);
    },
    fromIsDomain() {
      return this.config?.email?.from && this.config.email.from.badge1 === 'domain';
    },
    validate() {
      return this.$refs.validationObserver.validate();
    },
  },
  methods: {
    ...mapActions(['showToastMessage', 'fetchProjectVariables', 'fetchProjectEmailProviders']),
    // Keep defaultAgent valid as the selected agents change: clear it when none
    // remain, or default to the first selected when unset/removed.
    onAgentsChange(val) {
      const list = val || [];
      if (!list.length) {
        this.config.defaultAgent = '';
      } else if (!this.config.defaultAgent || !list.includes(this.config.defaultAgent)) {
        this.config.defaultAgent = list[0];
      }
    },
    hasConfigDataAdapter(adapter) {
      return this.config.adapters?.includes(adapter) || false;
    },
    onDataAdapterChange(value, adapter) {
      if (!value) {
        this.config.adapters = this.config.adapters.filter((i) => i !== adapter);
      } else {
        this.config.adapters.push(adapter);
      }
    },
    async save() {
      if (['conversations', 'collections'].includes(this.feature)) {
        const valid = await this.$refs.validationObserver.validate();
        if (!valid.valid) return;
      }

      if (this.feature === 'deep_research_reports') {
        this.config.reports = this.config.reports.filter((item) => item.enabled);
      }

      this.loading = true;
      this.$emit('save', { enabled: true, config: this.config });
    },
    cancel() {
      this.$emit('close');
    },
  },
};
</script>
